import {useCallback, useRef} from 'react';
import {OCRDetectedData} from '@chekin/core';
import {ANALYTIC_EVENTS, Analytics, IV_ERROR_CATEGORY} from '../../../analytics';
import {OCR_FAILURE_REASONS} from '../ocrFailure';
import {useOCR} from '../../useOCR';
import {OCRFlowActions, OCRFlowContext, OCRFlowHandlers} from './types';
import {
  buildOCRImageDetails,
  getDocumentCompletionEvent,
  resolveOCRFailure,
  shouldPersistOCRImage,
} from './helpers';

type UseOCRFlowProps = {
  context: OCRFlowContext;
  actions: OCRFlowActions;
  handlers: OCRFlowHandlers;
};

function useOCRFlow({context, actions, handlers}: UseOCRFlowProps) {
  const {
    docType,
    onlyScanning,
    isBiomatchAndPassport,
    documentType,
    frontSideImage,
    countryCode,
    reservationId,
    nationality,
    intentId,
  } = context;
  const {saveOCRData, saveOCRImageDetails, finishFlow} = actions;
  const {onError, onBefore, onRetryLimitReached, onFailed} = handlers;

  const handleOCRSuccess = useCallback(
    async (image: string, data?: OCRDetectedData) => {
      if (data) {
        await saveOCRData(data);
      }

      if (
        docType &&
        shouldPersistOCRImage({isBiomatchAndPassport, onlyScanning, docType})
      ) {
        const docTypeImageDetails = buildOCRImageDetails(image, docType, data);
        await saveOCRImageDetails(docType, docTypeImageDetails);

        const completionEvent = getDocumentCompletionEvent(docType);
        if (completionEvent) {
          Analytics.track(completionEvent);
        }
      }

      Analytics.track(ANALYTIC_EVENTS.ivFinishingDocumentMRZScan);

      finishFlow();
    },
    [
      docType,
      finishFlow,
      isBiomatchAndPassport,
      onlyScanning,
      saveOCRData,
      saveOCRImageDetails,
    ],
  );

  const hasRetriedFrontSideRef = useRef(false);

  const {sendAndVerifyImageForOCR} = useOCR({
    onRetriesFinish: onRetryLimitReached,
    onDetectionFailed: (failure, data) => {
      const failWith = () =>
        onFailed(
          failure.title,
          undefined,
          failure.reason === OCR_FAILURE_REASONS.serviceError
            ? IV_ERROR_CATEGORY.server
            : IV_ERROR_CATEGORY.documentNotRecognized,
          {description: failure.description, reason: failure.reason},
        );
      const resolution = resolveOCRFailure({
        documentTypeWithoutMrz: documentType?.without_mrz,
        documentTypeBothSidesMrz: documentType?.both_sides_mrz,
        hasFrontSideImage: Boolean(frontSideImage.image),
        docType,
      });

      if (resolution.type === 'success_without_mrz') {
        void handleOCRSuccess(data.image, {...data, mrz_detected: true});
        return;
      }

      if (resolution.type === 'retry_front_side_as_ocr') {
        if (!documentType?.value || hasRetriedFrontSideRef.current) {
          failWith();
          return;
        }

        hasRetriedFrontSideRef.current = true;
        void sendAndVerifyImageForOCR({
          documentType: documentType.value,
          nationality: nationality || '',
          image: frontSideImage.image || '',
          source: data.source || '',
          countryCode: countryCode || '',
          reservationId: reservationId || '',
        });
        return;
      }

      failWith();
    },
    onFinish: handleOCRSuccess,
    onError,
    onBefore,
    intentId,
  });

  const startOCRForNewPhoto: typeof sendAndVerifyImageForOCR = useCallback(
    (...args) => {
      hasRetriedFrontSideRef.current = false;
      return sendAndVerifyImageForOCR(...args);
    },
    [sendAndVerifyImageForOCR],
  );

  return {sendAndVerifyImageForOCR: startOCRForNewPhoto};
}

export {useOCRFlow};
